import { useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import filtersStyles from "../SignRequestsFilters.styles";
import { SignRequestsView } from "../SignRequestsFiltersHelper";
import {
  BookmarkGlyph,
  CaretDownGlyph,
  CheckGlyph,
  KebabGlyph,
  PlusGlyph,
} from "./SignRequestsIcons";

const styles = filtersStyles;

interface SignRequestsViewsMenuProps {
  views: SignRequestsView[];
  activeViewId: string | null;
  /** Saved views are still being read; the built-in ones are usable already. */
  loading: boolean;
  disabled: boolean;
  onSelect: (view: SignRequestsView) => void;
  /** Opens the "Save view" dialog for the filters currently applied. */
  onSave: () => void;
  onRename: (view: SignRequestsView) => void;
  onDelete: (view: SignRequestsView) => void;
  onManage: () => void;
}

/**
 * The Views trigger and its dropdown: pick a view, save the current filters as
 * one, or rename / delete a view from its own overflow menu.
 *
 * Built-in views carry no overflow menu — they cannot be renamed or deleted.
 */
const SignRequestsViewsMenu = ({
  views,
  activeViewId,
  loading,
  disabled,
  onSelect,
  onSave,
  onRename,
  onDelete,
  onManage,
}: SignRequestsViewsMenuProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [rowMenu, setRowMenu] = useState<{
    anchor: HTMLElement;
    view: SignRequestsView;
  } | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const activeView = views.find((view) => view.id === activeViewId);

  const close = () => {
    setAnchor(null);
    setRowMenu(null);
    setConfirmingId(null);
  };

  return (
    <>
      <ButtonBase
        sx={styles.viewsTrigger}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-label={activeView ? `View: ${activeView.name}` : "Views"}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <BookmarkGlyph sx={styles.triggerGlyph} />
        <Box component="span" sx={styles.triggerLabel}>
          {activeView ? activeView.name : "Views"}
        </Box>
        <CaretDownGlyph sx={styles.triggerCaret} />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        PaperProps={{ sx: styles.viewsMenuPaper }}
      >
        <Box sx={styles.menuSectionLabel}>
          Your views
          {loading && (
            <CircularProgress
              size={12}
              role="status"
              aria-label="Loading saved views"
              sx={styles.inlineSpinner}
            />
          )}
        </Box>
        {views.map((view) => {
          if (confirmingId === view.id) {
            return (
              <Box key={view.id} sx={styles.inlineConfirm}>
                <Box component="span" sx={styles.inlineConfirmText}>
                  Delete "{view.name}"?
                </Box>
                <Box sx={styles.inlineConfirmActions}>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => {
                      setConfirmingId(null);
                      onDelete(view);
                    }}
                  >
                    Delete
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setConfirmingId(null)}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            );
          }

          const active = view.id === activeViewId;
          return (
            <MenuItem
              key={view.id}
              role="menuitemradio"
              aria-checked={active}
              sx={styles.viewRowItem(active)}
              onClick={() => {
                close();
                onSelect(view);
              }}
            >
              <Box component="span" sx={styles.menuCheckSlot}>
                {active && <CheckGlyph sx={styles.menuCheck} />}
              </Box>
              <Box component="span" sx={styles.menuItemLabel}>
                {view.name}
              </Box>
              {!view.builtIn && (
                <IconButton
                  sx={styles.viewKebab}
                  aria-label={`Options for view ${view.name}`}
                  aria-haspopup="menu"
                  onClick={(event) => {
                    event.stopPropagation();
                    setRowMenu({ anchor: event.currentTarget, view });
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <KebabGlyph sx={styles.viewKebabGlyph} />
                </IconButton>
              )}
            </MenuItem>
          );
        })}

        <Box sx={styles.menuDivider} />
        <MenuItem
          sx={styles.saveViewItem}
          onClick={() => {
            close();
            onSave();
          }}
        >
          <PlusGlyph sx={styles.menuCheck} />
          <Box component="span" sx={styles.menuItemLabel}>
            Save current view…
          </Box>
        </MenuItem>
        <MenuItem
          sx={styles.manageViewsItem}
          onClick={() => {
            close();
            onManage();
          }}
        >
          Manage views
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={rowMenu?.anchor ?? null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
      >
        <MenuItem
          onClick={() => {
            const target = rowMenu?.view;
            close();
            if (target) onRename(target);
          }}
        >
          Rename
        </MenuItem>
        <MenuItem
          sx={styles.dangerItem}
          onClick={() => {
            setConfirmingId(rowMenu?.view.id ?? null);
            setRowMenu(null);
          }}
        >
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export default SignRequestsViewsMenu;
